import { useState, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { format, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import LoadingSpinner from '../components/LoadingSpinner';
import { fetcher } from '../lib/api';
import { InteractionTypeIcon } from '../components/InteractionTypeIcon';

interface ConversationTurn {
  role: 'user' | 'model';
  content: string;
  timestamp: string;
}

interface ConversationData {
  turns: ConversationTurn[];
  summary: {
    endTime: string;
    startTime: string;
    messageCount: number;
  };
}

interface Interaction {
  id: string;
  type: 'voice' | 'chat' | 'whatsapp' | 'phone';
  startTime: string;
  endTime: string | null;
  messageCount: number | null;
  audioSeconds: number | null;
}

interface FullInteraction extends Interaction {
  conversationData: ConversationData;
  metadata: any;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  interactions: Interaction[];
  totalPages: number;
  currentPage: number;
  startDate: string;
  endDate: string;
}

/**
 * @deprecated Legacy flat interaction history. Prefer `/customers` for customer-centric
 * conversation review. This page is kept temporarily as a secondary view and should be removed.
 */
export function History() {
  // Default date range (past month)
  const defaultStartDate = format(subMonths(new Date(), 1), 'yyyy-MM-dd');
  const defaultEndDate = format(new Date(), 'yyyy-MM-dd');
  
  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);
  
  const [queryStartDate, setQueryStartDate] = useState<string>(defaultStartDate);
  const [queryEndDate, setQueryEndDate] = useState<string>(defaultEndDate);
  const [page, setPage] = useState(1);
  const [selectedInteractionId, setSelectedInteractionId] = useState<string | null>(null);
  
  const formattedStartDate = new Date(`${queryStartDate}T00:00:00`).toISOString();
  const formattedEndDate = new Date(`${queryEndDate}T23:59:59`).toISOString();
  
  const { data, error, isLoading, mutate } = useSWR<PaginatedResponse>(
    `${import.meta.env.VITE_BACKEND_URL}/api/interactions?page=${page}&limit=10&startDate=${formattedStartDate}&endDate=${formattedEndDate}`,
    fetcher
  );

  useEffect(() => {
    if (data && (!queryStartDate || !queryEndDate)) {
      if (data.startDate) {
        setStartDate(format(new Date(data.startDate), 'yyyy-MM-dd'));
        setQueryStartDate(format(new Date(data.startDate), 'yyyy-MM-dd'));
      }
      if (data.endDate) {
        setEndDate(format(new Date(data.endDate), 'yyyy-MM-dd'));
        setQueryEndDate(format(new Date(data.endDate), 'yyyy-MM-dd'));
      }
    }
  }, [data, queryStartDate, queryEndDate]);

  const { data: selectedInteractionData, error: selectedInteractionError, isLoading: isLoadingInteraction } = 
    useSWR<FullInteraction>(
      selectedInteractionId 
        ? `${import.meta.env.VITE_BACKEND_URL}/api/interactions/${selectedInteractionId}` 
        : null,
      fetcher
    );

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setQueryStartDate(startDate);
    setQueryEndDate(endDate);
    mutate(); // Re-fetch with new date range
  };

  const closeModal = () => {
    setSelectedInteractionId(null);
  };

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selectedInteractionId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    modalRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedInteractionId]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-red-500">Failed to load interactions</div>
      </div>
    );
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, 'dd-MMM-yy, HH:mm', { locale: es });
  };

  const handleRowClick = (interaction: Interaction) => {
    if (interaction.type === 'chat' || interaction.type === 'whatsapp') {
      setSelectedInteractionId(interaction.id);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="mb-4 text-3xl font-bold text-primary">Historial de Interacciones</h1>
      
      {/* Date range filter */}
      <div className="mx-auto mb-6 max-w-3xl pt-4">
        <form onSubmit={handleFilterSubmit} className="flex flex-wrap items-end justify-center gap-4">
          <div className="flex flex-col">
            <label htmlFor="startDate" className="mb-1 text-sm text-gray-600">
              Fecha inicial
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              max={endDate}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="endDate" className="mb-1 text-sm text-gray-600">
              Fecha final
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              min={startDate}
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary px-6 py-2 font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Buscar
          </button>
        </form>
      </div>
      
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : data?.interactions.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-md">
          <p className="text-lg text-gray-500">No se encontraron interacciones en el periodo seleccionado.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Inicio</th>
                <th className="hidden px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 md:table-cell">Fin</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Mensajes</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Duración</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {data?.interactions.map((interaction) => (
                <tr 
                  key={interaction.id} 
                  className={`${(interaction.type === 'chat' || interaction.type === 'whatsapp') 
                    ? 'cursor-pointer hover:bg-gray-50' 
                    : ''}`}
                  onClick={() => handleRowClick(interaction)}
                >
                  <td className="whitespace-nowrap px-6 py-3">
                    <InteractionTypeIcon type={interaction.type} />
                  </td>
                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{formatDate(interaction.startTime)}</td>
                  <td className="hidden whitespace-nowrap px-6 py-3 text-sm text-gray-500 md:table-cell">
                    {interaction.endTime ? formatDate(interaction.endTime) : 'N/A'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{interaction.messageCount || 'N/A'}</td>
                  <td className="whitespace-nowrap px-6 py-3 text-sm text-gray-500">{formatDuration(interaction.audioSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <nav className="flex items-center rounded-lg bg-white px-4 py-3 text-gray-700 shadow-md">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="mr-2 rounded-md px-2 py-1 font-semibold hover:bg-gray-100 disabled:opacity-50"
              aria-label="Previous"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <span className="px-2 text-sm">
              Página <span className="font-medium">{page}</span> de <span className="font-medium">{data.totalPages}</span>
            </span>
            <button
              onClick={() => setPage(p => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
              className="ml-2 rounded-md px-2 py-1 font-semibold hover:bg-gray-100 disabled:opacity-50"
              aria-label="Next"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      )}

      {/* Conversation Modal */}
      {selectedInteractionId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="history-modal-title"
        >
          <div
            ref={modalRef}
            tabIndex={-1}
            className="w-full max-h-[80vh] max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl outline-none"
          >
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 id="history-modal-title" className="text-xl font-semibold">
                {selectedInteractionData?.type === 'chat' ? 'Conversación de Chat' : 'Conversación de WhatsApp'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Cerrar diálogo"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isLoadingInteraction ? (
              <div className="flex items-center justify-center p-8">
                <LoadingSpinner />
              </div>
            ) : selectedInteractionError ? (
              <div className="p-6 text-center text-red-500">
                Error loading conversation details
              </div>
            ) : (
              <div className="overflow-y-auto p-6" style={{ maxHeight: 'calc(80vh - 8rem)' }}>
                {selectedInteractionData?.conversationData ? (
                  <div className="space-y-4">
                    {selectedInteractionData.conversationData.turns?.map((turn, index) => (
                      <div key={index} className={`flex ${turn.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-md rounded-lg px-4 py-2 shadow ${
                          turn.role === 'user' 
                            ? 'bg-primary/10 text-primary/80' 
                            : 'bg-secondary/10 text-secondary/80'
                        }`}>
                          <p className="whitespace-pre-wrap text-sm">{turn.content}</p>
                          <p className="mt-1 text-right text-xs text-gray-500">
                            {turn.timestamp && formatDate(turn.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {!selectedInteractionData.conversationData.turns?.length && (
                      <p className="text-center text-gray-500">No hay datos de conversación disponibles</p>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-gray-500">No hay datos de conversación disponibles</p>
                )}
              </div>
            )}
            
            <div className="border-t bg-gray-50 px-6 py-4">
              <button
                onClick={closeModal}
                className="w-full rounded-md bg-primary px-4 py-2 font-semibold text-white shadow-sm hover:bg-primary/90"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 